/**
 * ----------------------------------------------------------------------------------------------------------------------
 * SEARCH DRAWER PANEL
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Drives the two-column predictive search panel inside `sections/search-drawer.liquid`.
 *
 * Design rule that shapes everything below: THE DOM IS BUILT ONCE. This element never replaces, re-mounts or
 * rebuilds its containers. Every state change writes `textContent` on the two titles and swaps the CHILDREN of
 * the two content holders, so container identity, the height lock and the ARIA wiring all survive untouched.
 *
 * That is also why this file exists at all rather than reusing Impact's `<predictive-search>`: that element swaps
 * shadow slots and calls `.replaceWith()` on its results node, which is precisely the rebuild we must avoid.
 *
 * State A is pure Liquid and costs no requests. Its two columns are INDEPENDENT — the left list is plain
 * navigation, and nothing in it drives the right grid. The only fetch this element ever makes is the predictive
 * search for State B.
 *
 * Accessibility is deliberately NOT reimplemented here. `<search-drawer>` extends Impact's `Drawer` ->
 * `DialogElement`, which already owns `role="dialog"`, `aria-modal`, the focus trap, Escape-to-close and focus
 * restoration to the trigger. This element only adds the combobox layer on top.
 */

(() => {
  if (window.customElements.get('search-panel')) return;

  const DEBOUNCE_MS = 250;
  /** Fallback only — the real value is merchant-controlled via `data-min-query-length`. */
  const DEFAULT_MIN_QUERY_LENGTH = 1;
  /* Must stay in step with the `.sdp__fade` transition duration in search-drawer-panel.css. */
  const FADE_MS = 220;
  const ANNOUNCE_THROTTLE_MS = 700;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class SearchPanel extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('search-drawer');

      this.input = this.querySelector('[data-sdp-input]');
      this.placeholderEl = this.querySelector('[data-sdp-placeholder]');
      this.body = this.querySelector('[data-sdp-body]');
      this.leftTitle = this.querySelector('[data-sdp-left-title]');
      this.rightTitle = this.querySelector('[data-sdp-right-title]');
      this.leftList = this.querySelector('[data-sdp-left]');
      this.rightGrid = this.querySelector('[data-sdp-right]');
      this.liveRegion = this.querySelector('[data-sdp-live]');
      this.clearButton = this.querySelector('[data-sdp-clear]');
      this.prevButton = this.querySelector('[data-sdp-prev]');
      this.nextButton = this.querySelector('[data-sdp-next]');
      this.form = this.querySelector('form');
      this.topSuggestion = '';

      if (!this.input || !this.leftList || !this.rightGrid) return;

      this.resultsSection = this.dataset.resultsSection;
      this.productLimit = parseInt(this.dataset.productLimit, 10) || 4;
      this.suggestionLimit = parseInt(this.dataset.suggestionLimit, 10) || 10;
      this.minQueryLength = Math.max(
        1,
        parseInt(this.dataset.minQueryLength, 10) || DEFAULT_MIN_QUERY_LENGTH
      );
      this.idleLeftTitle = this.dataset.idleLeftTitle || '';
      this.idleRightTitle = this.dataset.idleRightTitle || '';
      this.typingLeftTitle = this.dataset.typingLeftTitle || '';
      this.typingRightTitle = this.dataset.typingRightTitle || '';
      this.rotationMs = (parseInt(this.dataset.rotationSeconds, 10) || 4) * 1000;

      try {
        this.placeholders = JSON.parse(this.dataset.placeholders || '[]').filter(Boolean);
      } catch {
        this.placeholders = [];
      }

      this.state = 'idle';
      this.idleTemplate = null;
      this.heightLock = 0;
      this.requestToken = 0;
      this.virtualIndex = -1;
      this.rotationTimer = null;
      this.rotationIndex = 0;

      this.announceTimer = null;
      this.lastAnnounceAt = 0;

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.input.addEventListener('input', this.onInput.bind(this), { signal });
      this.input.addEventListener('keydown', this.onKeydown.bind(this), { signal });
      /* No pointerdown handler: clicking an empty field must not stop the rotation, since no `input` event
         follows a bare click and it would never restart. Typing is what stops it, via onInput. */
      this.clearButton?.addEventListener('click', this.onClear.bind(this), { signal });
      this.prevButton?.addEventListener('click', () => this.scrollResults(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.scrollResults(1), { signal });
      this.rightGrid.addEventListener('scroll', () => this.updateNavButtons(), { signal, passive: true });
      this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });
      this.input.addEventListener('search', this.onInput.bind(this), { signal });

      if (this.drawer) {
        this.drawer.addEventListener('dialog:after-show', this.onDrawerShow.bind(this), { signal });
        this.drawer.addEventListener('dialog:after-hide', this.onDrawerHide.bind(this), { signal });
      }

      window.addEventListener('resize', this.releaseHeightLock.bind(this), { signal });

      /** Only take over the native placeholder once we know we have something to replace it with. */
      if (this.placeholders.length > 0) {
        this.classList.add('sdp--custom-placeholder');
        this.showPlaceholder(this.placeholders[0]);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortInFlight();
      this.stopRotation();
      clearTimeout(this.debounceTimer);
      clearTimeout(this.announceTimer);
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Drawer lifecycle
     * -------------------------------------------------------------------------------------------------------------- */

    onDrawerShow() {
      this.captureIdleMarkup();
      this.applyHeightLock();
      this.observeHeight();

      this.startRotation();
    }

    onDrawerHide() {
      /** No orphaned timers: the interval dies with the drawer, every time. */
      this.stopRotation();
      clearTimeout(this.debounceTimer);
      this.heightObserver?.disconnect();
      this.heightObserver = null;
      this.releaseHeightLock();
    }

    /**
     * The lock has to reflect the cards as actually rendered — card height varies with which visibility
     * toggles are on, and images settle after first paint. Observing the body picks up both without ever
     * hardcoding a dimension. Growth is monotonic, so this converges immediately rather than oscillating.
     */
    observeHeight() {
      if (this.heightObserver || typeof ResizeObserver === 'undefined') return;
      this.heightObserver = new ResizeObserver(() => this.applyHeightLock());
      this.heightObserver.observe(this.body);
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Idle snapshot
     *
     * The Liquid-rendered idle columns are cloned once into a detached <template> and restored from that clone.
     * Guarded so it can only ever capture the pristine server markup, never a State B result set.
     * -------------------------------------------------------------------------------------------------------------- */

    captureIdleMarkup() {
      if (this.idleTemplate) return;

      this.idleTemplate = document.createElement('template');

      const left = document.createElement('div');
      left.setAttribute('data-sdp-snapshot-left', '');
      left.append(...Array.from(this.leftList.children).map((node) => node.cloneNode(true)));

      const right = document.createElement('div');
      right.setAttribute('data-sdp-snapshot-right', '');
      right.append(...Array.from(this.rightGrid.children).map((node) => node.cloneNode(true)));

      this.idleTemplate.content.append(left, right);
    }

    restoreIdleMarkup() {
      if (!this.idleTemplate) return;

      const clone = this.idleTemplate.content.cloneNode(true);
      const left = clone.querySelector('[data-sdp-snapshot-left]');
      const right = clone.querySelector('[data-sdp-snapshot-right]');

      /** replaceChildren swaps the contents while keeping the containers themselves alive. */
      this.leftList.replaceChildren(...Array.from(left.children));
      this.rightGrid.replaceChildren(...Array.from(right.children));
      this.leftTitle.textContent = this.idleLeftTitle;
      this.rightTitle.textContent = this.idleRightTitle;
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Height lock
     *
     * The lock only ever grows while the drawer is open, so idle / loading / results / no-results all measure the
     * same. It is released on close and on resize so a new viewport starts from its own natural height.
     * -------------------------------------------------------------------------------------------------------------- */

    applyHeightLock() {
      if (!this.body) return;
      const natural = Math.max(this.body.scrollHeight, this.projectTypingHeight());
      if (natural > this.heightLock) {
        this.heightLock = natural;
        this.body.style.minHeight = `${this.heightLock}px`;
      }
    }

    /**
     * Reserve the height State B will need, before the first search runs.
     *
     * A purely reactive lock still shifts once — on the very first transition, because idle is usually shorter
     * than a full result set. So we project the taller state from the CURRENTLY RENDERED elements: one list item
     * and one card are measured live, which means the projection automatically tracks the card-visibility
     * toggles (a card with rating and quick add on is taller, and the reservation grows with it). Nothing here
     * is a hardcoded dimension.
     */
    projectTypingHeight() {
      /**
       * Measure the TALLEST rendered example, not the first one. Grid rows size to their tallest card, so
       * projecting from card #1 under-reserves whenever a later card runs a line longer.
       */
      const tallest = (selector, root) =>
        Array.from(root.querySelectorAll(selector)).reduce(
          (max, el) => Math.max(max, el.getBoundingClientRect().height),
          0
        );

      const itemHeight = tallest('.sdp__item', this.leftList);
      const cardHeight = tallest('.sdp__card', this.rightGrid);
      if (!itemHeight || !cardHeight) return 0;

      const listGap = parseFloat(getComputedStyle(this.leftList).rowGap) || 0;
      const gridStyle = getComputedStyle(this.rightGrid);
      const gridGap = parseFloat(gridStyle.rowGap) || 0;

      /**
       * How many ROWS the results state will occupy — it cannot be read from the live grid, because this runs
       * while the panel is still idle and the two states lay out differently. Derived per breakpoint instead:
       *
       *   >= 990px   results scroll horizontally, always one row
       *   <= 749px   one row when the merchant chose the slider, otherwise a stacked grid that wraps
       *   between    the fixed 2-up grid
       */
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const wrapInto = (columns) => Math.max(1, Math.ceil(this.productLimit / Math.max(1, columns)));

      let rows = 1;
      if (isMobile) {
        if (this.dataset.mobileLayout === 'stacked') {
          rows = wrapInto(parseInt(getComputedStyle(this).getPropertyValue('--sdp-per-row-mobile'), 10) || 2);
        }
      } else if (!isDesktop) {
        rows = wrapInto(2);
      }

      /**
       * No slack is added to the card: the stylesheet reserves a fixed two-line title inside the drawer, so
       * every card here is exactly `cardHeight` tall regardless of which products the query returns.
       */
      const leftHeight = this.suggestionLimit * itemHeight + Math.max(0, this.suggestionLimit - 1) * listGap;
      const rightHeight = rows * cardHeight + Math.max(0, rows - 1) * gridGap;

      const titleStyle = getComputedStyle(this.leftTitle);
      const titleHeight =
        this.leftTitle.getBoundingClientRect().height + (parseFloat(titleStyle.marginBottom) || 0);

      /**
       * `body.scrollHeight` includes the body's own padding, so the projection has to as well. Deriving that
       * chrome by subtracting the current tallest column keeps it correct at any breakpoint, rather than
       * re-reading padding values that differ between the stacked and two-column layouts.
       */
      const leftColumn = titleHeight + leftHeight;
      const rightColumn = titleHeight + rightHeight;

      /**
       * Below 990px the two columns STACK, so the body is their sum plus the row gap — not the taller of the
       * two. Reserving `max()` there was over-reserving on phones by a whole column, which is where the large
       * band of empty space came from.
       */
      const isStacked = !isDesktop;
      const bodyGap = parseFloat(getComputedStyle(this.body).rowGap) || 0;
      const columnsHeight = isStacked
        ? leftColumn + bodyGap + rightColumn
        : Math.max(leftColumn, rightColumn);

      /**
       * `body.scrollHeight` includes the body's own padding, so the projection has to as well. Deriving that
       * chrome from the current layout keeps it correct at any breakpoint, rather than re-reading padding
       * values that differ between the stacked and two-column layouts.
       */
      const currentLeft = this.leftList.parentElement?.getBoundingClientRect().height || 0;
      const currentRight = this.rightGrid.parentElement?.getBoundingClientRect().height || 0;
      const currentContent = isStacked
        ? currentLeft + bodyGap + currentRight
        : Math.max(currentLeft, currentRight);
      const chrome = Math.max(0, this.body.scrollHeight - currentContent);

      return chrome + columnsHeight;
    }

    releaseHeightLock() {
      if (!this.body) return;
      this.heightLock = 0;
      this.body.style.minHeight = '';
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Input
     * -------------------------------------------------------------------------------------------------------------- */

    onInput() {
      const query = this.input.value.trim();

      this.togglePlaceholder(query.length === 0);
      if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
      /* Stop while there is text, resume the moment the field is emptied again. */
      if (query.length > 0) {
        this.stopRotation();
      } else {
        this.startRotation();
      }

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        if (query.length >= this.minQueryLength) {
          this.enterTypingState(query);
        } else {
          this.enterIdleState();
        }
      }, DEBOUNCE_MS);
    }

    /**
     * Reconcile the drawer's matching with the search page's.
     *
     * The two use different engines: predictive search matches PREFIXES, so "cell" surfaces "cello" here, but
     * /search?q=cell is a full-text match and returns nothing — the shopper sees products, clicks through, and
     * lands on an empty page. Submitting the top suggestion instead sends a term the results page can actually
     * find. With no suggestion to lean on we fall back to a trailing wildcard, which Shopify's search does
     * honour, so a partial word still returns something rather than nothing.
     *
     * This only ever rewrites what is SUBMITTED; the drawer keeps showing exactly what was typed.
     */
    onSubmit(event) {
      const typed = this.input.value.trim();

      if (typed.length === 0) {
        event.preventDefault();
        return;
      }

      if (!this.hasAttribute('data-correct-terms')) return;

      const suggestion = this.topSuggestion.trim();

      if (suggestion && suggestion.toLowerCase() !== typed.toLowerCase()) {
        this.input.value = suggestion;
        return;
      }

      /* Leave quoted or already-wildcarded queries alone — the shopper meant those literally. */
      if (!suggestion && !/[*"]/.test(typed)) {
        this.input.value = `${typed}*`;
      }
    }

    /**
     * Clearing routes through the same `input` path the keyboard uses, so there is exactly one code path back
     * to the idle state — no duplicated teardown to drift out of sync. Focus returns to the field so the
     * shopper can keep typing without reaching for the mouse.
     */
    onClear() {
      this.input.value = '';
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.focus();
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * State A — idle. No fetch, no derivation: just the stored clone.
     * -------------------------------------------------------------------------------------------------------------- */

    async enterIdleState() {
      /** Any in-flight predictive request is now stale. */
      this.abortInFlight();
      this.requestToken += 1;
      this.setLoading(false);

      if (this.state === 'idle') return;
      this.state = 'idle';
      this.dataset.state = 'idle';
      this.topSuggestion = '';
      this.setShowAllVisible(false);
      this.updateNavButtons();

      await this.crossFade(() => this.restoreIdleMarkup());

      this.resetVirtualFocus();
      this.applyHeightLock();
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * State B — typing
     * -------------------------------------------------------------------------------------------------------------- */

    async enterTypingState(query) {
      const token = this.startRequest();
      this.setLoading(true);

      /**
       * `resources[limit_scope]=each` applies one limit per resource type, so we request the larger of the two
       * configured limits and trim each column to its own limit after parsing.
       */
      const limit = Math.max(this.productLimit, this.suggestionLimit);
      const url =
        `${this.root()}search/suggest` +
        `?q=${encodeURIComponent(query)}` +
        `&section_id=${encodeURIComponent(this.resultsSection)}` +
        `&resources[type]=product,query` +
        `&resources[limit]=${limit}` +
        `&resources[limit_scope]=each`;

      const payload = await this.fetchPayload(url, token);

      /** A late response from an older keystroke can never overwrite a newer one. */
      if (token !== this.requestToken) return;
      this.setLoading(false);
      if (!payload) return;

      const suggestions = this.takeChildren(payload.querySelector('[data-sdp-left-items]'), this.suggestionLimit);
      const products = this.takeChildren(payload.querySelector('[data-sdp-right-items]'), this.productLimit);
      const hasSuggestions = suggestions.length > 0;
      /** An empty result set renders a single "no results" paragraph, not cards. */
      const hasProducts = products.some((node) => node.classList?.contains('sdp__card'));

      this.state = 'typing';
      this.dataset.state = 'typing';
      this.topSuggestion = payload.dataset.topSuggestion || '';

      /* Decode first, then fade — so the incoming cards arrive complete rather than filling in afterwards. */
      await this.preloadImages(products);
      if (token !== this.requestToken) return;

      await this.crossFade(() => {
        if (hasSuggestions) {
          this.leftTitle.textContent = this.typingLeftTitle;
          this.leftList.replaceChildren(...suggestions);
        } else {
          /** No suggestions: keep the idle collection list rather than leaving the column blank. */
          const clone = this.idleTemplate?.content.cloneNode(true);
          const left = clone?.querySelector('[data-sdp-snapshot-left]');
          this.leftTitle.textContent = this.idleLeftTitle;
          if (left) this.leftList.replaceChildren(...Array.from(left.children));
        }

        this.rightTitle.textContent = this.typingRightTitle;
        this.rightGrid.replaceChildren(...products);
        /** Reset the scroll position so a new query starts at the first card, not mid-row. */
        this.rightGrid.scrollLeft = 0;
      });

      this.setShowAllVisible(hasProducts);
      this.updateNavButtons();
      this.resetVirtualFocus();
      this.applyHeightLock();
      this.announce(payload.dataset.announce);
    }

    /**
     * Scroll the results row by one screenful. `scroll-behavior: smooth` on the grid animates it, and because
     * the buttons are absolutely positioned over the row, showing or hiding them cannot resize anything.
     */
    scrollResults(direction) {
      this.rightGrid.scrollBy({ left: direction * this.rightGrid.clientWidth, behavior: 'smooth' });
    }

    updateNavButtons() {
      if (!this.prevButton || !this.nextButton) return;

      const grid = this.rightGrid;
      /* 2px of tolerance: fractional widths mean scrollLeft rarely lands exactly on the end. */
      const overflows = grid.scrollWidth - grid.clientWidth > 2;
      const isTyping = this.state === 'typing';

      this.prevButton.hidden = !isTyping || !overflows || grid.scrollLeft <= 2;
      this.nextButton.hidden =
        !isTyping || !overflows || grid.scrollLeft >= grid.scrollWidth - grid.clientWidth - 2;
    }

    /** The button is part of the persistent DOM; showing it is an attribute flip, never a rebuild. */
    setShowAllVisible(isVisible) {
      this.showAll = this.showAll ?? this.querySelector('[data-sdp-actions]');
      if (this.showAll) this.showAll.hidden = !isVisible;
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Fetching
     * -------------------------------------------------------------------------------------------------------------- */

    root() {
      return window.Shopify?.routes?.root || '/';
    }

    startRequest() {
      this.abortInFlight();
      this.abortSignalController = new AbortController();
      this.requestToken += 1;
      return this.requestToken;
    }

    abortInFlight() {
      this.abortSignalController?.abort();
      this.abortSignalController = null;
    }

    async fetchPayload(url, token) {
      try {
        const response = await fetch(url, { signal: this.abortSignalController?.signal });
        if (!response.ok) return null;
        const markup = await response.text();
        if (token !== this.requestToken) return null;
        return new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-sdp-payload]');
      } catch (error) {
        if (error.name !== 'AbortError') console.error('[search-panel]', error);
        return null;
      }
    }

    /** Returns up to `limit` adopted child elements, ready to hand straight to replaceChildren. */
    takeChildren(container, limit) {
      if (!container) return [];
      return Array.from(container.children)
        .slice(0, limit)
        .map((child) => document.importNode(child, true));
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Transitions
     * -------------------------------------------------------------------------------------------------------------- */

    setLoading(isLoading) {
      /** Outgoing content stays in place at reduced opacity — the panel never collapses to zero height. */
      [this.leftList, this.rightGrid].forEach((el) => {
        el.dataset.loading = isLoading ? 'true' : 'false';
      });
    }

    /**
     * Wait for incoming card images to be decoded BEFORE they are swapped in.
     *
     * Without this the fade-in reveals empty image boxes that pop as each file lands, which reads as a jerky
     * swap however smooth the opacity transition is. Cards past the third are marked `loading="lazy"` by the
     * card snippet and would never fetch while detached, so they are switched to eager first.
     *
     * Capped by a timeout: a slow image must delay the swap a little, never block it.
     */
    async preloadImages(nodes, timeoutMs = 600) {
      const images = nodes.flatMap((node) =>
        node.querySelectorAll ? Array.from(node.querySelectorAll('img')) : []
      );
      if (images.length === 0) return;

      const decoded = images.map((img) => {
        img.loading = 'eager';
        return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      });

      await Promise.race([Promise.all(decoded), wait(timeoutMs)]);
    }

    async crossFade(mutate) {
      const targets = [this.leftList, this.rightGrid, this.leftTitle, this.rightTitle].filter(Boolean);

      if (prefersReducedMotion()) {
        mutate();
        return;
      }

      targets.forEach((el) => (el.dataset.fading = 'true'));
      await wait(FADE_MS);
      mutate();
      targets.forEach((el) => (el.dataset.fading = 'false'));
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Placeholder rotation
     * -------------------------------------------------------------------------------------------------------------- */

    showPlaceholder(text) {
      if (!this.placeholderEl) return;
      this.placeholderEl.textContent = text;
      this.placeholderEl.hidden = text.length === 0;
    }

    togglePlaceholder(shouldShow) {
      if (!this.placeholderEl) return;
      this.placeholderEl.hidden = !shouldShow || this.placeholderEl.textContent.length === 0;
    }

    /**
     * Rotate whenever the field is empty.
     *
     * This replaces the old "suspend until the drawer is reopened" rule, which left the placeholder frozen for
     * the rest of the session after a single keystroke. Focus alone no longer stops it either — the drawer
     * autofocuses the input on open, so a stop-on-focus rule killed the timer before it ever ticked.
     */
    startRotation() {
      /** A single placeholder is static content — never start a timer for it. */
      if (this.placeholders.length < 2 || this.input.value.length > 0) return;
      this.stopRotation();
      this.rotationTimer = setInterval(() => this.advancePlaceholder(), this.rotationMs);
    }

    stopRotation() {
      clearInterval(this.rotationTimer);
      /**
       * Cancel any fade already in flight too. Without this, a swap scheduled a moment before the shopper
       * started typing still lands, and the placeholder visibly jumps one more word after it should have
       * stopped. Resetting `fading` leaves the element at full opacity rather than stranded mid-fade.
       */
      clearTimeout(this.placeholderSwapTimer);
      this.rotationTimer = null;
      this.placeholderSwapTimer = null;
      /* Empty, not 'false' — the phase attribute now drives the swap, so clearing it returns the element to
         its resting state and lets any queued frame callback see that the swap was cancelled. */
      if (this.placeholderEl) this.placeholderEl.dataset.fading = '';
    }

    /**
     * Kept as the name the keyboard handler calls. It is now simply "stop" — there is no sticky suspended
     * state any more, because rotation resumes on its own as soon as the field is empty again.
     */
    suspendRotation() {
      this.stopRotation();
    }

    advancePlaceholder() {
      if (this.input.value.length > 0) return this.stopRotation();

      this.rotationIndex = (this.rotationIndex + 1) % this.placeholders.length;
      const next = this.placeholders[this.rotationIndex];

      if (prefersReducedMotion()) {
        this.showPlaceholder(next);
        return;
      }

      /**
       * Three phases, matching the CSS: leave upward (fast), reposition below with no transition, then
       * release so the new word rises and settles. Timers are held in fields so stopRotation() can cancel a
       * swap mid-flight.
       */
      const OUT_MS = 160; /* must match the exit duration in .sdp__placeholder[data-fading='out'] */

      this.placeholderEl.dataset.fading = 'out';

      this.placeholderSwapTimer = setTimeout(() => {
        this.showPlaceholder(next);
        this.placeholderEl.dataset.fading = 'in';

        /**
         * Two frames: the first lets the browser paint the 'in' start position, the second releases it.
         * Releasing in the same frame would collapse both states into one style resolution and the entry
         * would not animate at all.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.placeholderEl.dataset.fading === 'in') this.placeholderEl.dataset.fading = '';
          });
        });
      }, OUT_MS);
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Keyboard — combobox layer over the left column
     * -------------------------------------------------------------------------------------------------------------- */

    get options() {
      return Array.from(this.leftList.querySelectorAll('[role="option"]'));
    }

    onKeydown(event) {
      const options = this.options;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (options.length === 0) return;
          event.preventDefault();
          this.suspendRotation();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.virtualIndex = (this.virtualIndex + step + options.length) % options.length;
          this.moveVirtualFocus(options);
          break;
        }

        case 'Enter': {
          if (this.virtualIndex < 0 || !options[this.virtualIndex]) return; /* fall through to form submit */
          event.preventDefault();
          options[this.virtualIndex].click();
          break;
        }

        /** Escape is intentionally not handled: it belongs to DialogElement, which also restores focus. */
        default:
          break;
      }
    }

    moveVirtualFocus(options) {
      options.forEach((option, index) => {
        const isCurrent = index === this.virtualIndex;
        option.classList.toggle('is-virtual-focus', isCurrent);
        if (isCurrent) {
          if (!option.id) option.id = `sdp-option-${index}`;
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    resetVirtualFocus() {
      this.virtualIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.options.forEach((option) => option.classList.remove('is-virtual-focus'));
    }

    /* ----------------------------------------------------------------------------------------------------------------
     * Announcements — throttled so fast typing cannot flood a screen reader
     * -------------------------------------------------------------------------------------------------------------- */

    announce(message) {
      if (!this.liveRegion || !message) return;

      const elapsed = Date.now() - this.lastAnnounceAt;
      clearTimeout(this.announceTimer);

      if (elapsed >= ANNOUNCE_THROTTLE_MS) {
        this.lastAnnounceAt = Date.now();
        this.liveRegion.textContent = message;
        return;
      }

      this.announceTimer = setTimeout(() => {
        this.lastAnnounceAt = Date.now();
        this.liveRegion.textContent = message;
      }, ANNOUNCE_THROTTLE_MS - elapsed);
    }
  }

  window.customElements.define('search-panel', SearchPanel);
})();
